"use client";

import { css, styled } from "@mui/material/styles";
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { useField } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";

export interface FunnelSelectOption {
  label: string;
  value: string | number | boolean;
}

export type FunnelSelectAppearance = "default" | "questionnaire" | "hosted";

interface FunnelSelectProps extends Omit<SelectProps<string>, "onChange"> {
  options: FunnelSelectOption[];
  placeholder?: string;
  onChange?: (value: string) => void;
  appearance?: FunnelSelectAppearance;
}

/**
 * Styled select component for form funnel.
 * Shared 56px select treatment used by questionnaire and hosted funnel steps.
 */
export const FunnelSelect = ({
  options,
  placeholder = "Select one...",
  value,
  onChange,
  disabled,
  appearance = "default",
  ...props
}: FunnelSelectProps) => {
  const isBrandedFunnelSelect = appearance !== "default";
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange?.(event.target.value);
  };

  return (
    <StyledFormControl fullWidth disabled={disabled} $appearance={appearance}>
      <StyledSelect
        $appearance={appearance}
        variant={isBrandedFunnelSelect ? "outlined" : undefined}
        value={value as string}
        onChange={handleChange}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return <PlaceholderText $appearance={appearance}>{placeholder}</PlaceholderText>;
          }
          const selectedOption = options.find((opt) => opt.value.toString() === selected);
          return selectedOption?.label || selected;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: "8px",
              mt: 1,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            },
          },
        }}
        {...props}
        {...(isBrandedFunnelSelect && { IconComponent: SelectChevron })}
      >
        {options.map((option) => (
          <StyledMenuItem key={option.value.toString()} value={option.value.toString()}>
            {option.label}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </StyledFormControl>
  );
};

interface FunnelSelectWithFormikProps extends Omit<FunnelSelectProps, "value" | "onChange"> {
  name: string;
  onChange?: (value: string) => void;
}

/**
 * Formik-integrated version of FunnelSelect.
 * Automatically connects to Formik context via field name.
 */
export const FunnelSelectWithFormik = ({
  name,
  onChange,
  ...selectProps
}: FunnelSelectWithFormikProps) => {
  const [field, , helpers] = useField(name);

  const handleChange = async (value: string) => {
    await helpers.setValue(value);
    await helpers.setTouched(true);
    onChange?.(value);
  };

  return (
    <FunnelSelect
      {...selectProps}
      value={field.value || ""}
      onChange={handleChange}
      onBlur={field.onBlur}
      name={name}
    />
  );
};

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== "$appearance",
})<{ $appearance: FunnelSelectAppearance }>`
  max-width: ${({ $appearance }) => ($appearance === "default" ? "320px" : "none")};
  width: 100%;
`;

const StyledSelect = styled(Select<string>, {
  shouldForwardProp: (prop) => prop !== "$appearance",
})<{ $appearance: FunnelSelectAppearance }>`
  border-radius: 8px;
  background-color: white;
  font-size: ${({ $appearance }) => ($appearance === "default" ? "16px" : "1.1rem")};
  height: ${({ $appearance }) => ($appearance === "default" ? "auto" : "56px")};

  & .MuiOutlinedInput-notchedOutline {
    border: ${({ theme, $appearance }) =>
      $appearance === "default"
        ? `2px solid ${theme.palette.primary.dark}`
        : `1px solid ${theme.palette.dream.borderColor}`};
    border-radius: 8px;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme, $appearance }) =>
      $appearance === "default" ? theme.palette.primary.dark : theme.palette.primary.main};
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.palette.primary.main};
    border-width: 2px;
    box-shadow: ${({ theme, $appearance }) =>
      $appearance === "default" ? `0 0 0 2px ${theme.palette.primary.main}33` : "none"};
  }

  & .MuiSelect-select {
    height: ${({ $appearance }) => ($appearance === "default" ? "auto" : "56px")};
    min-height: ${({ $appearance }) => ($appearance === "default" ? "48px" : "auto")};
    padding: ${({ $appearance }) => ($appearance === "default" ? "16px 18px" : "0 44px 0 16px")};
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  &.Mui-disabled {
    background-color: ${({ theme, $appearance }) =>
      $appearance === "default" ? "#f5f5f5" : theme.palette.dream.grayLighter};

    & .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme, $appearance }) =>
        $appearance === "default" ? "#b0b0b0" : theme.palette.dream.borderColor};
    }
  }

  ${({ theme, $appearance }) =>
    $appearance !== "default" &&
    css`
      & .MuiSelect-icon {
        right: 16px;
        color: ${theme.palette.dream.grayMedium};
        font-size: 14px;
      }
    `}
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 16px;
  padding: 12px 18px;

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.main}0A;
  }

  &.Mui-selected {
    background-color: ${({ theme }) => theme.palette.primary.main}14;

    &:hover {
      background-color: ${({ theme }) => theme.palette.primary.main}1F;
    }
  }
`;

const PlaceholderText = styled("span", {
  shouldForwardProp: (prop) => prop !== "$appearance",
})<{ $appearance: FunnelSelectAppearance }>`
  color: ${({ theme, $appearance }) =>
    $appearance === "default" ? "#7c7978" : theme.palette.dream.grayMedium};
`;

const SelectChevron = ({ className }: { className?: string }) => (
  <FontAwesomeIcon className={className} icon={faChevronDown} />
);
